import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {authHeader} from "../services/auth-header";
import {
    COMPUTING_ADMIN_API_URL,
    filter,
    getNextSort,
    getValue,
    parseNull,
    sort
} from "../containers/canopia/CanopiaUtils";

const initialSortStatus = {
    "bmKey": "desc",
    "bmName": "asc",
    "status": "asc",
    "re": "asc",
    "min": "asc",
    "max": "asc",
    "median": "asc",
    "lastBM.dateFmt": "asc",
    "lastBM.lastUpdateFmt": "asc",
    "lastBM.nbPos": "asc",
    "histoLength": "asc"
}

const sortAndFilterInitialState = {
    sortStatus: initialSortStatus,
    sortCol: "bmKey",
    filterValuesOrig: {}, // all the values of the filters corresponding to tgtDataOrig (initialized once, immutable then)
    filterValues: {}, // all the values of the filters
    filterSelectedValues: {}, // the current selected values of the filters
}

const initialState = {
    benchmarks: [],
    bmStatus: "idle",
    bmError: null,

    // BM status
    tgtDataOrig: null,
    tgtData: [], // The displayed ones, filtered, sorted, ...
    bmStatusStatus: "idle",
    bmStatusError: null,

    ...sortAndFilterInitialState
}

const filterCols = { //
    'status': 0, // 0 => the filter is inactive, 1 => 1st active filter, ...
    'lastBM.dateFmt': 0, // 0 => the filter is inactive, 1 => 1st active filter, ...
};

function resetFilters(state) {
    state.filterValues = state.filterValuesOrig;
    state.filterSelectedValues = state.filterValuesOrig;
    Object.keys(filterCols).forEach(filterCol => {
        filterCols[filterCol] = 0;
    });
}

export const loadBM = createAsyncThunk(
    'benchmark/load-bm',
    async () =>
        fetch(COMPUTING_ADMIN_API_URL + "benchmark", {
            headers: authHeader(),
            cache: 'no-store'
        })
            .then(response => {
                let contentType = response.headers.get("content-type");
                if (contentType && contentType.indexOf("application/json") !== -1) {
                    return response.json();
                } else {
                    console.log("The content type is not JSON, found " + contentType);
                }
            })
            .then(json => json)
            .catch(error => error)
);

export const loadBMStatus = createAsyncThunk(
    'benchmark/load-status',
    async () =>
        fetch(COMPUTING_ADMIN_API_URL + "benchmark-status", {
            headers: authHeader(),
            cache: 'no-store'
        })
            .then(response => {
                let contentType = response.headers.get("content-type");
                if (contentType && contentType.indexOf("application/json") !== -1) {
                    return response.json();
                } else {
                    console.log("The content type is not JSON, found " + contentType);
                }
            })
            .then(json => json)
            .catch(error => error)
);

export const benchmarkSlice = createSlice({
    name: 'benchmark',
    initialState,
    reducers: {
        sortBMStatus: (state, action) => {
            // load the portfolio of the selected client
            let col = action.payload.col;

            sort(state, col, initialSortStatus);
        },
        filterBMStatus: (state, action) => {
            // Update the filters with the new selection
            const colName = action.payload.colName;
            const selectedValues = action.payload.selectedValues;

            filter(state, colName, filterCols, selectedValues, initialSortStatus);
        },
        clearAllBMStatusFilters: (state, action) => {
            state.tgtData = state.tgtDataOrig;

            resetFilters(state);

            let prevSort = state.sortStatus[state.sortCol];
            state.sortStatus[state.sortCol] = getNextSort(prevSort);
            sort(state, state.sortCol, initialSortStatus);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadBM.pending, (state, action) => {
                state.bmStatus = 'loading';
            })
            .addCase(loadBM.fulfilled, (state, action) => {
                let payload = action.payload;
                if (payload.status) {
                    state.bmStatus = 'error';
                    state.bmError = payload;
                } else if (payload.message === 'Failed to fetch') {
                    state.bmStatus = 'error';
                    state.bmError = {
                        error: 'Service unavailable', //
                        message: 'We apologize for the inconvenience, our team is working on solving the issue. ' + //
                            'Please try to come back in a few minutes. Thank you for your patience.'
                    };
                } else {
                    state.bmStatus = 'success';
                    state.benchmarks = payload["data"];
                }
            })
            .addCase(loadBM.rejected, (state, action) => {
                state.bmStatus = 'error';
                state.bmError = action.payload;
            })
            .addCase(loadBMStatus.pending, (state, action) => {
                state.bmStatusStatus = 'loading';
            })
            .addCase(loadBMStatus.fulfilled, (state, action) => {
                let payload = action.payload;
                if (payload.status) {
                    state.bmStatusStatus = 'error';
                    state.bmStatusError = payload;
                } else if (payload.message === 'Failed to fetch') {
                    state.bmStatusStatus = 'error';
                    state.bmStatusError = {
                        error: 'Service unavailable', //
                        message: 'We apologize for the inconvenience, our team is working on solving the issue. ' + //
                            'Please try to come back in a few minutes. Thank you for your patience.'
                    };
                } else {
                    state.bmStatusStatus = 'success';
                    state.tgtDataOrig = payload["data"];
                    state.tgtData = [...state.tgtDataOrig];

                    // Reset the sort and filter
                    state = Object.assign(state, sortAndFilterInitialState);

                    if (state.tgtDataOrig) {
                        const tmpValues = {};
                        Object.keys(filterCols).forEach(filterCol => {
                            tmpValues[filterCol] = [];
                        });
                        state.tgtDataOrig.forEach(dLine => {
                            Object.keys(filterCols).forEach(filterCol => {
                                let cols = filterCol.split('.');
                                const value = parseNull(getValue(dLine, cols));
                                if (!tmpValues[filterCol].includes(value)) {
                                    tmpValues[filterCol].push(value);
                                }
                            });
                        });
                        Object.keys(tmpValues).forEach(filterCol => {
                            tmpValues[filterCol].sort();
                        });
                        state.filterValuesOrig = tmpValues;

                        resetFilters(state);

                        sort(state, state.sortCol, initialSortStatus);
                    }
                }
            })
            .addCase(loadBMStatus.rejected, (state, action) => {
                state.bmStatusStatus = 'error';
                state.bmStatusError = action.payload;
            })
    }
});

export const {
    sortBMStatus,
    filterBMStatus,
    clearAllBMStatusFilters
} = benchmarkSlice.actions;

export const selectBenchmarkState = state => state.benchmark;

export default benchmarkSlice.reducer;